import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from 'store/store';
import { SocialNet, SocialNetType } from 'modules/organizer/domain/interfaces/social-net';
import type { UpsertSocialNetInput } from 'modules/organizer/domain/interfaces/upsert-social-net-input';
import organizerRepository from 'modules/organizer/domain/repositories/organizer-repository';

const PREFIX = 'socialNets';

export const fetchSocialNets = createAsyncThunk<SocialNet[], void>(
  `${PREFIX}/fetchSocialNets`,
  async (payload, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const tournamentId = state.auth.activeTournamentId;
      if (!tournamentId) {
        throw new Error('Tournament not found');
      }

      return await organizerRepository.fetchSocialNet(tournamentId);
    } catch (error) {
      console.error(`${PREFIX}/fetchSocialNets error:`, error);

      let message;
      if (error instanceof Error) {
        message = error.message;
      } else {
        message = String(error);
      }
      return rejectWithValue(message);
    }
  },
);

export interface SaveSocialNetsPayload {
  website?: string;
  vk?: string;
  'vk-video'?: string;
  telegram?: string;
  youtube?: string;
  instagram?: string;
}

export const upsertSocialNets = createAsyncThunk<SocialNet[], SaveSocialNetsPayload>(
  `${PREFIX}/saveSocialNets`,
  async (payload, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const tournamentId = state.auth.activeTournamentId;
      if (!tournamentId) {
        throw new Error('Tournament not found');
      }

      // Fetch all existing social networks once
      const existingSocialNets = await organizerRepository.fetchSocialNet(tournamentId);
      const savedSocialNets: Promise<SocialNet>[] = [];

      // Save website (tournament URL)
      if (payload.website !== undefined) {
        const existingWebsite = existingSocialNets.find((sn) => sn.type === SocialNetType.TOURNAMENT_SITE);

        // Only send the mutation if the URL has changed
        if (!existingWebsite || existingWebsite.url !== payload.website) {
          const input: UpsertSocialNetInput = {
            id: existingWebsite?.id,
            tournamentId,
            type: SocialNetType.TOURNAMENT_SITE,
            url: payload.website,
          };
          const savedWebsite = organizerRepository.upsertSocialNet(input);
          savedSocialNets.push(savedWebsite);
        } else {
          // If URL hasn't changed, just add the existing social network to the result
          savedSocialNets.push(Promise.resolve(existingWebsite));
        }
      }

      // Save VK
      if (payload.vk !== undefined) {
        const existingVk = existingSocialNets.find((sn) => sn.type === SocialNetType.VK);

        // Only send the mutation if the URL has changed
        if (!existingVk || existingVk.url !== payload.vk) {
          const input: UpsertSocialNetInput = {
            id: existingVk?.id,
            tournamentId,
            type: SocialNetType.VK,
            url: payload.vk,
          };
          const savedVk = organizerRepository.upsertSocialNet(input);
          savedSocialNets.push(savedVk);
        } else {
          // If URL hasn't changed, just add the existing social network to the result
          savedSocialNets.push(Promise.resolve(existingVk));
        }
      }

      // Save VK Video
      if (payload['vk-video'] !== undefined) {
        const existingVkVideo = existingSocialNets.find((sn) => sn.type === SocialNetType.VK_VIDEO);

        // Only send the mutation if the URL has changed
        if (!existingVkVideo || existingVkVideo.url !== payload['vk-video']) {
          const input: UpsertSocialNetInput = {
            id: existingVkVideo?.id,
            tournamentId,
            type: SocialNetType.VK_VIDEO,
            url: payload['vk-video'],
          };
          const savedVkVideo = organizerRepository.upsertSocialNet(input);
          savedSocialNets.push(savedVkVideo);
        } else {
          // If URL hasn't changed, just add the existing social network to the result
          savedSocialNets.push(Promise.resolve(existingVkVideo));
        }
      }

      // Save Telegram
      if (payload.telegram !== undefined) {
        const existingTelegram = existingSocialNets.find((sn) => sn.type === SocialNetType.TELEGRAM);

        // Only send the mutation if the URL has changed
        if (!existingTelegram || existingTelegram.url !== payload.telegram) {
          const input: UpsertSocialNetInput = {
            id: existingTelegram?.id,
            tournamentId,
            type: SocialNetType.TELEGRAM,
            url: payload.telegram,
          };
          const savedTelegram = organizerRepository.upsertSocialNet(input);
          savedSocialNets.push(savedTelegram);
        } else {
          // If URL hasn't changed, just add the existing social network to the result
          savedSocialNets.push(Promise.resolve(existingTelegram));
        }
      }

      // Save YouTube
      if (payload.youtube !== undefined) {
        const existingYoutube = existingSocialNets.find((sn) => sn.type === SocialNetType.YOUTUBE);

        // Only send the mutation if the URL has changed
        if (!existingYoutube || existingYoutube.url !== payload.youtube) {
          const input: UpsertSocialNetInput = {
            id: existingYoutube?.id,
            tournamentId,
            type: SocialNetType.YOUTUBE,
            url: payload.youtube,
          };
          const savedYoutube = organizerRepository.upsertSocialNet(input);
          savedSocialNets.push(savedYoutube);
        } else {
          // If URL hasn't changed, just add the existing social network to the result
          savedSocialNets.push(Promise.resolve(existingYoutube));
        }
      }

      // Save Instagram
      if (payload.instagram !== undefined) {
        const existingInstagram = existingSocialNets.find((sn) => sn.type === SocialNetType.INSTAGRAM);

        // Only send the mutation if the URL has changed
        if (!existingInstagram || existingInstagram.url !== payload.instagram) {
          const input: UpsertSocialNetInput = {
            id: existingInstagram?.id,
            tournamentId,
            type: SocialNetType.INSTAGRAM,
            url: payload.instagram,
          };
          const savedInstagram = organizerRepository.upsertSocialNet(input);
          savedSocialNets.push(savedInstagram);
        } else {
          // If URL hasn't changed, just add the existing social network to the result
          savedSocialNets.push(Promise.resolve(existingInstagram));
        }
      }

      return await Promise.all(savedSocialNets);
    } catch (error) {
      console.error(`${PREFIX}/saveSocialNets error:`, error);

      let message;
      if (error instanceof Error) {
        message = error.message;
      } else {
        message = String(error);
      }
      return rejectWithValue(message);
    }
  },
);
